import en from '../../../../lang/en-us';
import {
    accountNumberRegex,
    accountNumberValidation,
    ACCOUNT_TYPE_MASTER_DATA,
    emailRegex,
    isAlphaNumeric,
    MAX_LENGTH_FOR_GUARDIAN_NAME,
    minDateForCalender,
    MODE_OF_HOLDING_MASTER_DATA,
    TAX_RESIDENCY_MASTER_DATA,
    validateIfsc,
} from '../../../../utils/constants';
import { getTaxStatusCode } from '../../portfolios/utils';
const { dashboard, common } = en;

export interface CorrectionFolioSelection<T> {
    type?: 'text' | 'number' | 'dob' | 'number';
    label: string;
    isSelected?: boolean;
    value?: T;
    newValue?: T;
    field: string;
    validate?: (params: { value: T; newValue: T }) => T;
    handleChangeCondition?: (newValue: T) => T;
    isNominee?: boolean;
    shouldValidateField?: boolean;
    masterData?: Array<string>;
}

const joinName = (name: string) => {
    if (name) {
        return name.split(' ').join('').toLowerCase();
    }
    return '';
};

export const validateName = (oldName: string, newName: string) => {
    const match = /\d/.test(newName);
    if (newName.trim().length < 5) {
        return {
            error: true,
            helperText: dashboard.CorrectionFolios.Selection.Fields.errors.name.minFiveCharacters,
        };
    }
    if (match) {
        return {
            error: true,
            helperText: dashboard.CorrectionFolios.Selection.Fields.errors.name.noNumericValues,
        };
    }
    if (joinName(oldName) === joinName(newName)) {
        return {
            error: true,
            helperText: dashboard.CorrectionFolios.Selection.Fields.errors.name.sameNames,
        };
    }
    return { error: false, helperText: '' };
};

export const validatePrimaryHolder = (oldName: string, newName: string) => {
    const match = /\d/.test(newName);
    if (newName.trim().length < 5) {
        return {
            error: true,
            helperText: dashboard.CorrectionFolios.Selection.Fields.errors.name.minFiveCharacters,
        };
    }
    if (newName.trim().length > 100) {
        return {
            error: true,
            helperText:
                dashboard.CorrectionFolios.Selection.Fields.errors.name.maxHundredCharacters,
        };
    }
    if (match) {
        return {
            error: true,
            helperText: dashboard.CorrectionFolios.Selection.Fields.errors.name.noNumericValues,
        };
    }
    if (joinName(oldName) === joinName(newName)) {
        return {
            error: true,
            helperText: dashboard.CorrectionFolios.Selection.Fields.errors.name.sameNames,
        };
    }
    return { error: false, helperText: '' };
};

export const ifscRegex = /^[A-Za-z]{4}0[A-Z0-9a-z]{6}$/;

export const initSelection: CorrectionFolioSelection<any>[] = [
    {
        label: dashboard.CorrectionFolios.Selection.Fields.primaryHolder,
        type: 'text',
        isSelected: false,
        newValue: '',
        field: 'investorName',
        validate: ({ value, newValue }) => {
            if (newValue) {
                return validatePrimaryHolder(value, newValue);
            }
            return { error: false, helperText: '' };
        },
        handleChangeCondition: (newValue: any) => {
            if (isAlphaNumeric(newValue) || !newValue) {
                return true;
            }
        },
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.secondHolder,
        field: 'secondHolder',
        type: 'text',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue) {
                return validateName(value, newValue);
            }
            return { error: false, helperText: '' };
        },
        handleChangeCondition: (newValue: any) => {
            if (isAlphaNumeric(newValue) || !newValue) {
                return true;
            }
        },
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.thirdHolder,
        field: 'thirdHolder',
        type: 'text',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue) {
                return validateName(value, newValue);
            }
            return { error: false, helperText: '' };
        },
        handleChangeCondition: (newValue: any) => {
            if (isAlphaNumeric(newValue) || !newValue) {
                return true;
            }
        },
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.guardianName,
        field: 'guardian_name',
        type: 'text',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue) {
                return validateName(value, newValue);
            }
            return { error: false, helperText: '' };
        },
        handleChangeCondition: (newValue: any) => {
            if (isAlphaNumeric(newValue) || !newValue) {
                return true;
            }
        },
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.mobileNo,
        field: 'mobile',
        type: 'number',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue) {
                if (newValue[0] == 0) {
                    return {
                        error: true,
                        helperText:
                            dashboard.CorrectionFolios.Selection.Fields.errors.mobile
                                .startingWithZero,
                    };
                }
                if (/\D/.test(newValue) || newValue.length !== 10) {
                    return {
                        error: true,
                        helperText:
                            dashboard.CorrectionFolios.Selection.Fields.errors.mobile.validNumber,
                    };
                }
                return { error: false, helperText: '' };
            }
            return { error: false, helperText: '' };
        },
        handleChangeCondition: (newValue: any) => {
            if ((/^\d+$/.test(newValue) || !newValue) && newValue.length < 11) {
                return true;
            }
        },
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.dateOfBirth,
        field: 'dob',
        type: 'dob',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue) {
                const currentDate = new Date();
                if (currentDate < new Date(newValue)) {
                    return { error: true, helperText: common.errors.dob.minDOBValidation };
                }
                if (new Date(newValue) < new Date(minDateForCalender())) {
                    return { error: true, helperText: common.errors.dob.ageLessThanHundred };
                }
            }
            return { error: false, helperText: '' };
        },
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.bankAccNo,
        field: 'bank_accountNo',
        type: 'text',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue) {
                if (!accountNumberRegex.test(newValue)) {
                    return {
                        error: true,
                        helperText: common.errors.bankErrors.accountNo.regexError,
                    };
                }
            }
            return { error: false, helperText: '' };
        },
        handleChangeCondition: (newValue: any) => {
            if (accountNumberValidation(newValue)) {
                return true;
            }
        },
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.bankAccType,
        field: 'bank_accountType',
        type: 'text',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue && newValue === 'Select') {
                return { error: true, helperText: common.errors.bankErrors.accountType };
            }
            return { error: false, helperText: '' };
        },
        masterData: ACCOUNT_TYPE_MASTER_DATA,
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.bankIfsc,
        field: 'bank_ifsc',
        type: 'text',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue) {
                const match = ifscRegex.test(newValue);
                if (!match) {
                    return { error: true, helperText: common.errors.bankErrors.ifsc.regex };
                }
            }
            return { error: false, helperText: '' };
        },
        handleChangeCondition: (newValue) => {
            if (validateIfsc(newValue)) {
                return true;
            }
            return false;
        },
        shouldValidateField: true,
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.taxResidency,
        field: 'taxStatus',
        type: 'text',
        isSelected: false,
        newValue: '',
        masterData: Object.values(getTaxStatusCode),
        validate: ({ value, newValue }) => {
            if (newValue === 'Select') {
                return { error: true, helperText: common.errors.taxResidency };
            }
            return { error: false, helperText: '' };
        },
    },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.modeOfHolding,
        field: 'modeOfHolding',
        type: 'text',
        isSelected: false,
        newValue: '',
        masterData: MODE_OF_HOLDING_MASTER_DATA,
        validate: ({ value, newValue }) => {
            if (newValue === 'Select') {
                return { error: true, helperText: common.errors.modeOfHolding };
            }
            return { error: false, helperText: '' };
        },
    },
    // {
    //     label: dashboard.CorrectionFolios.Selection.Fields.dpIdNdClientId,
    //     field : 'dpid',
    //     type: 'text',
    //     isSelected : false,
    //     newValue : '',
    // },
    {
        label: dashboard.CorrectionFolios.Selection.Fields.email,
        field: 'email',
        type: 'text',
        isSelected: false,
        newValue: '',
        validate: ({ value, newValue }) => {
            if (newValue && !newValue.match(emailRegex)) {
                return { error: true, helperText: common.errors.email.regex };
            }
            return { error: false, helperText: '' };
        },
    },
];
